import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assessment,
  ClearAll,
  Download,
  AccountBalance,
  CalendarMonth,
  Print,
  Person,
  DeleteOutline,
  FilterList,
  EventAvailable,
} from '@mui/icons-material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import {
  EasyCheckPageContainer,
  EasyCheckPageHeader,
  EasyCheckPageContent,
  EasyCheckDataGridBasic,
  EasyCheckButton,
  EasyCheckToggleButton,
} from '../common/Controls';
import EasyCheckDatePicker from '../common/Controls/EasyCheckDatePicker';
import { useTheme } from '@mui/material/styles';
import { chequeService } from '../../services/cheques/chequeService';
import { useBankService } from '../../services/banks/bankService';
import { companyPreferencesService } from '../../services/settings/companyPreferencesService';
import { useSnackbar } from '../../hooks/useSnackbar';
import { format } from 'date-fns';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { exportToExcel } from '../../utils/excelExport';
import { formatCurrencyAmount, getCurrencySymbol, getCurrencyDecimals } from '../../utils/currencyUtils';

const STATUS_COLORS = {
  Pending: { bg: '#fef3c7', color: '#d97706' },
  Printed: { bg: '#ede9fe', color: '#7c3aed' },
  Signed: { bg: '#ede7f6', color: '#5e35b1' },
  Cleared: { bg: '#d1fae5', color: '#059669' },
  Cancelled: { bg: '#fee2e2', color: '#dc2626' },
  Bounced: { bg: '#fce7f3', color: '#be185d' },
  Collected: { bg: '#dbeafe', color: '#2563eb' },
};

const ReportsPage = () => {
  const theme = useTheme();
  const { showError } = useSnackbar();
  const bankService = useBankService();

  // State for filters
  const [filters, setFilters] = useState({
    printDateFrom: null,
    printDateTo: null,
    chqDateFrom: null,
    chqDateTo: null,
    collectedDateFrom: null,
    collectedDateTo: null,
    bankName: 'all',
    user: 'all',
    status: 'all',
    reportType: 'summary'
  });

  // State for data
  const [allCheques, setAllCheques] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [crossTabData, setCrossTabData] = useState([]);
  const [deletedCheques, setDeletedCheques] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signedStatusEnabled, setSignedStatusEnabled] = useState(false);
  const [collectionEnabled, setCollectionEnabled] = useState(false);

  // Optional modules — only expose the Signed filter / Collected Date filter when enabled
  useEffect(() => {
    let active = true;
    companyPreferencesService.getPreferences().then((result) => {
      if (active && result.success) {
        setSignedStatusEnabled(Boolean(result.data?.enableSignedStatus));
        setCollectionEnabled(Boolean(result.data?.enableChequeCollection));
      }
    });
    return () => { active = false; };
  }, []);

  // Load banks
  const loadBanks = async () => {
    try {
      const result = await bankService.getAllBanks(1, 10000);

      if (result.success || result.Success) {
        const data = result.data || result.Data || [];
        const banksData = Array.isArray(data) ? data.map(bank => ({
          id: bank.Id || bank.id,
          accountNumber: bank.AccountNumber || bank.accountNumber,
          accountName: bank.AccountName || bank.accountName,
          currency: bank.Currency || bank.currency,
          balance: bank.Balance || bank.balance || 0,
          companyId: bank.CompanyId || bank.companyId,
          isActive: bank.IsActive !== undefined ? bank.IsActive : bank.isActive,
          isDefault: bank.IsDefault !== undefined ? bank.IsDefault : bank.isDefault,
          hasChequeTemplate: bank.HasChequeTemplate !== undefined ? bank.HasChequeTemplate : bank.hasChequeTemplate || false,
          createdDate: bank.CreatedDate || bank.createdDate,
          modifiedDate: bank.ModifiedDate || bank.modifiedDate,
          createdUser: bank.CreatedUser || bank.createdUser,
          modifiedUser: bank.ModifiedUser || bank.modifiedUser
        })) : [];

        setBanks(banksData);
      } else {
        setBanks([]);
        showError('Failed to load banks. Please try again.');
      }
    } catch (error) {
      console.error('Error loading banks:', error);
      showError('Failed to load banks');
      setBanks([]);
    }
  };

  // Load all cheques
  const loadAllCheques = async () => {
    setLoading(true);
    try {
      const response = await chequeService.getPrintedCheques({
        pageNumber: 1,
        pageSize: 10000,
        status: ''
      });

      if (response.success && response.data) {
        const mappedCheques = response.data.map((cheque) => ({
          id: cheque.Id || cheque.id,
          ChequeNumber: cheque.ChequeNumber || cheque.chequeNumber,
          BankId: cheque.BankId || cheque.bankId,
          BankName: cheque.BankName || cheque.bankName,
          CompanyId: cheque.CompanyId || cheque.companyId,
          BeneficiaryName: cheque.BeneficiaryName || cheque.beneficiaryName,
          chqAmount: cheque.ChqAmount || cheque.chqAmount,
          AmountInWords: cheque.AmountInWords || cheque.amountInWords,
          ChqDate: cheque.ChqDate || cheque.chqDate,
          PrintDate: cheque.PrintDate || cheque.printDate,
          Remarks: cheque.Remarks || cheque.remarks,
          Status: cheque.Status || cheque.status,
          IsActive: cheque.IsActive !== undefined ? cheque.IsActive : cheque.isActive,
          CreatedAt: cheque.CreatedAt || cheque.createdAt,
          UpdatedAt: cheque.UpdatedAt || cheque.updatedAt,
          CreatedBy: cheque.CreatedBy || cheque.createdBy,
          UpdatedBy: cheque.UpdatedBy || cheque.updatedBy,
          ClearedDate: cheque.ClearedDate || cheque.clearedDate,
          CollectedDate: cheque.CollectedDate || cheque.collectedDate
        }));

        setAllCheques(mappedCheques);
        setReportData(mappedCheques);
        setError(null);
      } else {
        setAllCheques([]);
        setReportData([]);
        setError('Failed to load cheques');
      }
    } catch (error) {
      console.error('Error loading cheques:', error);
      setAllCheques([]);
      setReportData([]);
      setError('Failed to load cheques');
      showError('Failed to load cheque data');
    } finally {
      setLoading(false);
    }
  };

  // Load deleted cheques
  const loadDeletedCheques = async () => {
    setLoading(true);
    try {
      const response = await chequeService.getDeletedCheques({
        pageNumber: 1,
        pageSize: 10000,
      });

      if (response.success && response.data) {
        const mappedCheques = response.data.map((cheque) => ({
          id: cheque.Id || cheque.id,
          ChequeNumber: cheque.ChequeNumber || cheque.chequeNumber,
          BankId: cheque.BankId || cheque.bankId,
          BankName: cheque.BankName || cheque.bankName,
          CompanyId: cheque.CompanyId || cheque.companyId,
          BeneficiaryName: cheque.BeneficiaryName || cheque.beneficiaryName,
          chqAmount: cheque.ChqAmount || cheque.chqAmount,
          AmountInWords: cheque.AmountInWords || cheque.amountInWords,
          ChqDate: cheque.ChqDate || cheque.chqDate,
          PrintDate: cheque.PrintDate || cheque.printDate,
          Remarks: cheque.Remarks || cheque.remarks,
          Status: cheque.Status || cheque.status,
          IsActive: cheque.IsActive !== undefined ? cheque.IsActive : cheque.isActive,
          CreatedAt: cheque.CreatedAt || cheque.createdAt,
          UpdatedAt: cheque.UpdatedAt || cheque.updatedAt,
          CreatedBy: cheque.CreatedBy || cheque.createdBy,
          UpdatedBy: cheque.UpdatedBy || cheque.updatedBy,
          ClearedDate: cheque.ClearedDate || cheque.clearedDate,
          CollectedDate: cheque.CollectedDate || cheque.collectedDate,
          DeletedDate: cheque.UpdatedAt || cheque.updatedAt,
          DeletedBy: cheque.UpdatedBy || cheque.updatedBy,
        }));

        setDeletedCheques(mappedCheques);
        setReportData(mappedCheques);
        setError(null);
      } else {
        setDeletedCheques([]);
        setReportData([]);
        setError('Failed to load deleted cheques');
      }
    } catch (error) {
      console.error('Error loading deleted cheques:', error);
      setDeletedCheques([]);
      setError('Failed to load deleted cheques');
      showError('Failed to load deleted cheque data');
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      if (!isMounted) return;
      try {
        await Promise.all([loadBanks(), loadAllCheques()]);
      } catch (error) {
        console.error('Error in loadInitialData:', error);
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // When switching to deleted report type, load deleted cheques if not already loaded
    if (field === 'reportType' && value === 'deleted' && deletedCheques.length === 0) {
      loadDeletedCheques();
    }
    // When switching back from deleted, restore active cheques as report data
    if (field === 'reportType' && value !== 'deleted') {
      setReportData(allCheques);
    }
  };

  // Date range comparison helper
  const isDateInRange = (dateToCheck, fromDate, toDate) => {
    if (!dateToCheck) return false;

    const checkDate = new Date(dateToCheck);
    if (isNaN(checkDate.getTime())) return false;

    const checkDateOnly = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());

    if (fromDate) {
      const fromDateObj = new Date(fromDate);
      if (isNaN(fromDateObj.getTime())) return false;
      const fromDateOnly = new Date(fromDateObj.getFullYear(), fromDateObj.getMonth(), fromDateObj.getDate());
      if (checkDateOnly < fromDateOnly) return false;
    }

    if (toDate) {
      const toDateObj = new Date(toDate);
      if (isNaN(toDateObj.getTime())) return false;
      const toDateOnly = new Date(toDateObj.getFullYear(), toDateObj.getMonth(), toDateObj.getDate());
      if (checkDateOnly > toDateOnly) return false;
    }

    return true;
  };

  // Filter cheques based on criteria
  const filterCheques = (cheques, filtersToApply, banksData) => {
    return cheques.filter(cheque => {
      if (filtersToApply.printDateFrom || filtersToApply.printDateTo) {
        if (!isDateInRange(cheque.PrintDate, filtersToApply.printDateFrom, filtersToApply.printDateTo)) {
          return false;
        }
      }

      if (filtersToApply.chqDateFrom || filtersToApply.chqDateTo) {
        if (!isDateInRange(cheque.ChqDate, filtersToApply.chqDateFrom, filtersToApply.chqDateTo)) {
          return false;
        }
      }

      // Collected date filter — cheques never collected have no CollectedDate and are
      // excluded whenever this filter is active (isDateInRange returns false for null).
      if (filtersToApply.collectedDateFrom || filtersToApply.collectedDateTo) {
        if (!isDateInRange(cheque.CollectedDate, filtersToApply.collectedDateFrom, filtersToApply.collectedDateTo)) {
          return false;
        }
      }

      if (filtersToApply.bankName && filtersToApply.bankName !== 'all') {
        const selectedBank = banksData.find(bank => bank.accountNumber === filtersToApply.bankName);
        if (selectedBank) {
          if (cheque.BankId && selectedBank.id && cheque.BankId !== selectedBank.id) {
            return false;
          } else if (!cheque.BankId || !selectedBank.id) {
            const chequeBankName = cheque.BankName || '';
            const bankMatches = chequeBankName.toLowerCase().includes(selectedBank.accountName.toLowerCase()) ||
                               chequeBankName.toLowerCase().includes(selectedBank.accountNumber.toLowerCase());
            if (!bankMatches) return false;
          }
        }
      }

      if (filtersToApply.user && filtersToApply.user !== 'all') {
        if (cheque.CreatedBy !== filtersToApply.user) return false;
      }

      if (filtersToApply.status && filtersToApply.status !== 'all') {
        if (cheque.Status !== filtersToApply.status) return false;
      }

      return true;
    });
  };

  // Generate CrossTab data
  const generateCrossTabData = (filteredCheques) => {
    const monthYears = new Set();
    filteredCheques.forEach(cheque => {
      const chqDate = new Date(cheque.ChqDate);
      const monthYear = chqDate.toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric'
      });
      monthYears.add(monthYear);
    });

    const sortedMonthYears = Array.from(monthYears).sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB;
    });

    return filteredCheques.map((cheque) => {
      const chqDate = new Date(cheque.ChqDate);
      const chequeMonthYear = chqDate.toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric'
      });
      const amount = Number(cheque.chqAmount || 0);

      const row = {
        id: cheque.id,
        ChequeNumber: cheque.ChequeNumber,
        BeneficiaryName: cheque.BeneficiaryName,
        BankName: cheque.BankName,
        ChqDate: cheque.ChqDate,
        PrintDate: cheque.PrintDate,
        Status: cheque.Status,
        ClearedDate: cheque.ClearedDate,
        CreatedBy: cheque.CreatedBy,
        Remarks: cheque.Remarks,
        Total: amount
      };

      sortedMonthYears.forEach(monthYear => {
        row[monthYear] = (monthYear === chequeMonthYear) ? amount : 0;
      });

      return row;
    });
  };

  // Apply filters
  const applyFiltersWithData = (filtersToUse, chequesData) => {
    setError(null);

    try {
      if (filtersToUse.reportType === 'deleted') {
        const filteredDeleted = filterCheques(deletedCheques, filtersToUse, banks);
        setReportData(filteredDeleted);
        setCrossTabData([]);
        return;
      }

      const filteredCheques = filterCheques(chequesData, filtersToUse, banks);
      if (filtersToUse.reportType === 'summary') {
        setReportData(filteredCheques);
        setCrossTabData([]);
      } else if (filtersToUse.reportType === 'crosstab') {
        const crossTab = generateCrossTabData(filteredCheques);
        setCrossTabData(crossTab);
        setReportData([]);
      }
    } catch (error) {
      setError(error.message || 'Error generating report');
      console.error('Error generating report:', error);
    }
  };

  // Generate report (manual trigger)
  const generateReport = () => {
    if (filters.reportType === 'deleted') {
      if (deletedCheques.length === 0) {
        loadDeletedCheques();
      } else {
        applyFiltersWithData(filters, deletedCheques);
      }
    } else {
      applyFiltersWithData(filters, allCheques);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    const defaultFilters = {
      printDateFrom: null,
      printDateTo: null,
      chqDateFrom: null,
      chqDateTo: null,
      collectedDateFrom: null,
      collectedDateTo: null,
      bankName: 'all',
      user: 'all',
      status: 'all',
      reportType: 'summary'
    };
    setFilters(defaultFilters);
    setReportData(allCheques);
    setCrossTabData([]);
    setDeletedCheques([]);
  };

  // Excel export
  const handleExcelExport = useCallback(() => {
    const dataToExport = filters.reportType === 'crosstab' ? crossTabData : reportData;
    if (!dataToExport.length) return;

    const isDeleted = filters.reportType === 'deleted';
    const isCrossTab = filters.reportType === 'crosstab';
    const sheetName = isDeleted ? 'Deleted Cheques' : isCrossTab ? 'CrossTab' : 'Summary';

    // Build parameters from active filters
    const parameters = [];
    if (filters.printDateFrom || filters.printDateTo) {
      const from = filters.printDateFrom ? formatDateForDisplay(filters.printDateFrom) : 'Any';
      const to = filters.printDateTo ? formatDateForDisplay(filters.printDateTo) : 'Any';
      parameters.push({ label: 'Print Date Range', value: `${from} to ${to}` });
    }
    if (filters.chqDateFrom || filters.chqDateTo) {
      const from = filters.chqDateFrom ? formatDateForDisplay(filters.chqDateFrom) : 'Any';
      const to = filters.chqDateTo ? formatDateForDisplay(filters.chqDateTo) : 'Any';
      parameters.push({ label: 'Cheque Date Range', value: `${from} to ${to}` });
    }
    if (filters.collectedDateFrom || filters.collectedDateTo) {
      const from = filters.collectedDateFrom ? formatDateForDisplay(filters.collectedDateFrom) : 'Any';
      const to = filters.collectedDateTo ? formatDateForDisplay(filters.collectedDateTo) : 'Any';
      parameters.push({ label: 'Collected Date Range', value: `${from} to ${to}` });
    }
    if (filters.bankName && filters.bankName !== 'all') {
      const bankObj = banks.find(b => b.accountName === filters.bankName);
      parameters.push({ label: 'Bank', value: bankObj ? `${bankObj.accountName} (${bankObj.accountNumber})` : filters.bankName });
    }
    if (filters.user && filters.user !== 'all') {
      parameters.push({ label: 'User', value: filters.user });
    }
    if (filters.status && filters.status !== 'all') {
      parameters.push({ label: 'Status', value: filters.status });
    }
    parameters.push({ label: 'Report Type', value: isDeleted ? 'Deleted Cheques' : isCrossTab ? 'Cross Tab' : 'Summary' });

    // Build columns
    const baseColumns = [
      { header: 'Bank Name', key: 'BankName', type: 'text' },
      { header: 'Print Date', key: 'PrintDate', type: 'date' },
      { header: 'Chq No', key: 'ChequeNumber', type: 'text' },
      { header: 'Chq Date', key: 'ChqDate', type: 'date' },
      { header: 'Beneficiary', key: 'BeneficiaryName', type: 'text', width: 30 },
      { header: 'Status', key: 'Status', type: 'text' },
      { header: 'Cleared Date', key: 'ClearedDate', type: 'date' },
      { header: 'Printed By', key: 'CreatedBy', type: 'text' },
      { header: 'Remarks', key: 'Remarks', type: 'text', width: 25 },
    ];

    let columns, exportData, totals;

    // Decimals for amount columns: use the single currency in the data, else 2 (mixed).
    const exportCurrencies = new Set(
      dataToExport.map((r) => banks.find((b) => String(b.id) === String(r.BankId))?.currency || 'AED')
    );
    const exportDecimals = exportCurrencies.size === 1 ? getCurrencyDecimals([...exportCurrencies][0]) : 2;

    if (isDeleted) {
      columns = [
        { header: 'Bank Name', key: 'BankName', type: 'text' },
        { header: 'Chq No', key: 'ChequeNumber', type: 'text' },
        { header: 'Chq Date', key: 'ChqDate', type: 'date' },
        { header: 'Beneficiary', key: 'BeneficiaryName', type: 'text', width: 30 },
        { header: 'Status at Deletion', key: 'Status', type: 'text' },
        { header: 'Deleted By', key: 'DeletedBy', type: 'text' },
        { header: 'Deleted Date', key: 'DeletedDate', type: 'date' },
        { header: 'Print Date', key: 'PrintDate', type: 'date' },
        { header: 'Printed By', key: 'CreatedBy', type: 'text' },
        { header: 'Remarks', key: 'Remarks', type: 'text', width: 25 },
        { header: 'Amount', key: 'chqAmount', type: 'amount', decimals: exportDecimals },
      ];
      exportData = dataToExport.map(row => ({
        ...row,
        ChqDate: row.ChqDate ? formatDateForDisplay(row.ChqDate) : '',
        PrintDate: row.PrintDate ? formatDateForDisplay(row.PrintDate) : '',
        DeletedDate: row.DeletedDate ? formatDateForDisplay(row.DeletedDate) : '',
        chqAmount: Number(row.chqAmount || 0),
      }));
      const totalAmount = dataToExport.reduce((sum, row) => sum + (Number(row.chqAmount) || 0), 0);
      totals = { 'Amount': totalAmount };
    } else if (isCrossTab) {
      // Find dynamic month columns
      const monthKeys = Object.keys(dataToExport[0] || {}).filter(
        key => !['id', 'ChequeNumber', 'BeneficiaryName', 'BankName', 'ChqDate', 'PrintDate', 'Status', 'Remarks', 'Total', 'ClearedDate', 'CreatedBy'].includes(key)
      );
      const monthCols = monthKeys.map(key => ({ header: key, key, type: 'amount', decimals: exportDecimals }));
      columns = [...baseColumns, ...monthCols, { header: 'Total', key: 'Total', type: 'amount', decimals: exportDecimals }];

      exportData = dataToExport.map(row => ({
        ...row,
        PrintDate: row.PrintDate ? formatDateForDisplay(row.PrintDate) : '',
        ChqDate: row.ChqDate ? formatDateForDisplay(row.ChqDate) : '',
      }));

      const grandTotal = dataToExport.reduce((sum, row) => sum + (Number(row.Total) || 0), 0);
      totals = { 'Total': grandTotal };
      monthKeys.forEach(key => {
        totals[key] = dataToExport.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
      });
    } else {
      const summaryCols = [...baseColumns];
      if (collectionEnabled) {
        // After 'Cleared Date' (index 6)
        summaryCols.splice(7, 0, { header: 'Collected Date', key: 'CollectedDate', type: 'date' });
      }
      columns = [...summaryCols, { header: 'Amount', key: 'chqAmount', type: 'amount', decimals: exportDecimals }];
      exportData = dataToExport.map(row => ({
        ...row,
        PrintDate: row.PrintDate ? formatDateForDisplay(row.PrintDate) : '',
        ChqDate: row.ChqDate ? formatDateForDisplay(row.ChqDate) : '',
        ClearedDate: row.ClearedDate ? formatDateForDisplay(row.ClearedDate) : '',
        CollectedDate: row.CollectedDate ? formatDateForDisplay(row.CollectedDate) : '',
        chqAmount: Number(row.chqAmount || 0),
      }));
      const totalAmount = dataToExport.reduce((sum, row) => sum + (Number(row.chqAmount) || 0), 0);
      totals = { 'Amount': totalAmount };
    }

    exportToExcel({
      title: isDeleted ? `Deleted Cheques Report` : `Cheque Report - ${sheetName}`,
      fileName: `EasyCheck_${isDeleted ? 'Deleted_Cheques' : 'Report'}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`,
      sheetName,
      columns,
      data: exportData,
      parameters,
      totals,
    });
  }, [filters, crossTabData, reportData, deletedCheques, banks, collectionEnabled]);

  // Custom Toolbar
  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
      </Box>
      <GridToolbarQuickFilter
        debounceMs={300}
        sx={{ width: 260 }}
      />
    </GridToolbarContainer>
  );

  // Distinct users for filter dropdown
  const distinctUsers = useMemo(() => {
    const allUsers = [...allCheques.map(c => c.CreatedBy), ...deletedCheques.map(c => c.CreatedBy)];
    return [...new Set(allUsers.filter(Boolean))].sort();
  }, [allCheques, deletedCheques]);

  // Status counts from current report data
  const statusCounts = useMemo(() => {
    const data = filters.reportType === 'crosstab' ? crossTabData : reportData;
    return {
      cleared: data.filter(c => c.Status === 'Cleared').length,
      uncleared: data.filter(c => c.Status === 'Pending' || c.Status === 'Printed').length,
      cancelled: data.filter(c => c.Status === 'Cancelled').length,
      pdc: data.filter(c => {
        const chqDate = new Date(c.ChqDate);
        return chqDate > new Date() && c.Status !== 'Cancelled' && c.Status !== 'Bounced';
      }).length,
      bounced: data.filter(c => c.Status === 'Bounced').length,
    };
  }, [reportData, crossTabData, filters.reportType]);

  // Prepare rows
  const rows = useMemo(() => {
    const dataToShow = filters.reportType === 'crosstab' ? crossTabData : reportData;
    return Array.isArray(dataToShow) ? dataToShow.map((item, index) => ({
      id: item.id || index,
      ...item
    })) : [];
  }, [reportData, crossTabData, filters.reportType]);

  // Resolve a report row's currency from its bank (rows can span multiple banks/currencies).
  const getRowCurrency = (row) =>
    (banks.find((b) => String(b.id) === String(row?.BankId))?.currency) || 'AED';

  // Currency for column-total headers: the single currency in the report, else AED (mixed).
  const reportCurrency = (() => {
    const set = new Set(reportData.map((r) => getRowCurrency(r)));
    return set.size === 1 ? [...set][0] : 'AED';
  })();

  // Summary columns
  const summaryColumns = [
    {
      field: 'BankName',
      headerName: 'Bank Name',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'PrintDate',
      headerName: 'Print Date',
      width: 120,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? formatDateForDisplay(params.value) : '-'}
        </Typography>
      ),
      valueGetter: (value) => value ? new Date(value) : null,
    },
    {
      field: 'ChequeNumber',
      headerName: 'Chq No',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'ChqDate',
      headerName: 'Chq Date',
      width: 120,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDateForDisplay(params.value) : '-'}
        </Typography>
      ),
      valueGetter: (value) => value ? new Date(value) : null,
    },
    {
      field: 'BeneficiaryName',
      headerName: 'Beneficiary',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'Status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = params.value || 'Pending';
        const colors = STATUS_COLORS[status] || STATUS_COLORS.Pending;
        return (
          <Chip
            label={status}
            size="small"
            sx={{
              backgroundColor: colors.bg,
              color: colors.color,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
            }}
          />
        );
      },
    },
    {
      field: 'ClearedDate',
      headerName: 'Cleared Date',
      width: 120,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? formatDateForDisplay(params.value) : '-'}
        </Typography>
      ),
      valueGetter: (value) => value ? new Date(value) : null,
    },
    // Collected Date — only when the Cheque Collection module is enabled
    ...(collectionEnabled ? [{
      field: 'CollectedDate',
      headerName: 'Collected Date',
      width: 130,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? formatDateForDisplay(params.value) : '-'}
        </Typography>
      ),
      valueGetter: (value) => value ? new Date(value) : null,
    }] : []),
    {
      field: 'CreatedBy',
      headerName: 'Printed By',
      width: 150,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'Remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={params.value}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'chqAmount',
      headerName: 'Amount',
      width: 160,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary">
          {params.value != null
            ? formatCurrencyAmount(params.value, getRowCurrency(params.row))
            : formatCurrencyAmount(0, getRowCurrency(params.row))
          }
        </Typography>
      ),
      renderHeader: () => {
        const total = reportData.reduce((sum, row) => {
          const amount = Number(row.chqAmount || 0);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

        return (
          <Box sx={{ textAlign: 'right', lineHeight: 1.2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              Amount
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.7rem', display: 'block' }}>
              Total: {formatCurrencyAmount(total, reportCurrency)}
            </Typography>
          </Box>
        );
      },
    },
  ];

  // CrossTab columns
  const generateCrossTabColumns = () => {
    if (!crossTabData || crossTabData.length === 0) return [];

    const columns = [
      {
        field: 'BankName',
        headerName: 'Bank Name',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}
            title={params.value}
          >
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'PrintDate',
        headerName: 'Print Date',
        flex: 1,
        type: 'date',
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value ? formatDateForDisplay(params.value) : '-'}
          </Typography>
        ),
        valueGetter: (value) => value ? new Date(value) : null,
      },
      {
        field: 'ChequeNumber',
        headerName: 'Chq No',
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'ChqDate',
        headerName: 'Chq Date',
        flex: 1,
        type: 'date',
        renderCell: (params) => (
          <Typography variant="body2">
            {params.value ? formatDateForDisplay(params.value) : '-'}
          </Typography>
        ),
        valueGetter: (value) => value ? new Date(value) : null,
      },
      {
        field: 'BeneficiaryName',
        headerName: 'Beneficiary',
        flex: 1,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={params.value}
          >
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'Remarks',
        headerName: 'Remarks',
        flex: 1,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={params.value}
          >
            {params.value || '-'}
          </Typography>
        ),
      },
    ];

    // Collect dynamic month-year columns
    const monthYears = new Set();
    crossTabData.forEach(row => {
      Object.keys(row).forEach(key => {
        if (!['id', 'ChequeNumber', 'BeneficiaryName', 'BankName', 'ChqDate', 'PrintDate', 'Status', 'Remarks', 'Total', 'ClearedDate', 'CreatedBy'].includes(key)) {
          monthYears.add(key);
        }
      });
    });

    const sortedMonthYears = Array.from(monthYears).sort((a, b) => new Date(a) - new Date(b));

    // Column totals
    const columnTotals = {};
    sortedMonthYears.forEach(monthYear => {
      columnTotals[monthYear] = crossTabData.reduce((sum, row) => {
        const amount = Number(row[monthYear] || 0);
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
    });

    sortedMonthYears.forEach((monthYear) => {
      const total = columnTotals[monthYear];

      columns.push({
        field: monthYear,
        headerName: monthYear,
        flex: 1,
        minWidth: 140,
        type: 'number',
        align: 'right',
        headerAlign: 'center',
        renderCell: (params) => (
          <Typography
            variant="body2"
            fontWeight="bold"
            color="primary"
            sx={{ fontSize: sortedMonthYears.length > 8 ? '0.75rem' : '0.875rem' }}
          >
            {params.value != null && params.value > 0
              ? formatCurrencyAmount(params.value, reportCurrency)
              : ''
            }
          </Typography>
        ),
        renderHeader: () => (
          <Box sx={{ textAlign: 'center', lineHeight: 1.1, px: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                display: 'block',
                fontSize: sortedMonthYears.length > 8 ? '0.65rem' : '0.75rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={monthYear}
            >
              {monthYear}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                fontSize: sortedMonthYears.length > 8 ? '0.6rem' : '0.7rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={`Total: ${getCurrencySymbol(reportCurrency)} ${total.toLocaleString()}`}
            >
              {getCurrencySymbol(reportCurrency)} {total > 1000000 ? `${(total / 1000000).toFixed(1)}M` : total.toLocaleString()}
            </Typography>
          </Box>
        ),
      });
    });

    // Grand total column
    const grandTotal = crossTabData.reduce((sum, row) => {
      const amount = Number(row.Total || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    columns.push({
      field: 'Total',
      headerName: 'Total',
      flex: 1,
      minWidth: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary">
          {params.value != null
            ? formatCurrencyAmount(params.value || 0, reportCurrency)
            : formatCurrencyAmount(0, reportCurrency)
          }
        </Typography>
      ),
      renderHeader: () => (
        <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
            Total
          </Typography>
          <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.7rem', display: 'block' }}>
            Grand: {getCurrencySymbol(reportCurrency)} {grandTotal.toLocaleString()}
          </Typography>
        </Box>
      ),
    });

    return columns;
  };

  // Deleted cheques columns
  const deletedColumns = [
    {
      field: 'BankName',
      headerName: 'Bank Name',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={params.value}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'ChequeNumber',
      headerName: 'Chq No',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'ChqDate',
      headerName: 'Chq Date',
      width: 120,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDateForDisplay(params.value) : '-'}
        </Typography>
      ),
      valueGetter: (value) => value ? new Date(value) : null,
    },
    {
      field: 'BeneficiaryName',
      headerName: 'Beneficiary',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={params.value}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'chqAmount',
      headerName: 'Amount',
      width: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary">
          {params.value != null
            ? formatCurrencyAmount(params.value, getRowCurrency(params.row))
            : formatCurrencyAmount(0, getRowCurrency(params.row))
          }
        </Typography>
      ),
    },
    {
      field: 'Status',
      headerName: 'Status at Deletion',
      width: 140,
      renderCell: (params) => {
        const status = params.value || 'Pending';
        const colors = STATUS_COLORS[status] || STATUS_COLORS.Pending;
        return (
          <Chip
            label={status}
            size="small"
            sx={{
              backgroundColor: colors.bg,
              color: colors.color,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
            }}
          />
        );
      },
    },
    {
      field: 'DeletedBy',
      headerName: 'Deleted By',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={params.value}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'DeletedDate',
      headerName: 'Deleted Date',
      width: 140,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2" color="error.main" fontWeight={500}>
          {params.value ? formatDateForDisplay(params.value) : '-'}
        </Typography>
      ),
      valueGetter: (value) => value ? new Date(value) : null,
    },
    {
      field: 'Remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={params.value}>
          {params.value || '-'}
        </Typography>
      ),
    },
  ];

  const currentColumns = filters.reportType === 'deleted' ? deletedColumns : filters.reportType === 'crosstab' ? generateCrossTabColumns() : summaryColumns;

  // Report type toggle options
  const reportTypeOptions = [
    { value: 'summary', label: 'Summary' },
    { value: 'deleted', label: 'Deleted' },
  ];

  return (
    <EasyCheckPageContainer>
      {/* Page Header — title + Export only */}
      <EasyCheckPageHeader
        title="Reports"
        sx={{ py: 2 }}
        actions={[
          <EasyCheckButton
            key="export"
            buttonType="action"
            variant="outlined"
            onClick={handleExcelExport}
            startIcon={<Download />}
            size="small"
            disabled={!(filters.reportType === 'crosstab' ? crossTabData : reportData).length}
          >
            Export Excel
          </EasyCheckButton>,
        ]}
      />

      <EasyCheckPageContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Filter Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            border: `1px solid ${theme.palette.grey[200]}`,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ flexWrap: 'wrap', gap: 1.5 }}
          >
            {/* Report Type Toggle */}
            <EasyCheckToggleButton
              value={filters.reportType}
              onChange={(event, val) => { if (val !== null) handleFilterChange('reportType', val); }}
              options={reportTypeOptions}
              buttonType="action"
              size="small"
              exclusive
            />

            <Divider orientation="vertical" flexItem />

            {/* Print Date Range Group */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Print sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  Print Date
                </Typography>
              </Stack>
              <EasyCheckDatePicker
                label="From"
                value={filters.printDateFrom}
                onChange={(date) => handleFilterChange('printDateFrom', date)}
                format="dd-MM-yyyy"
                size="small"
                slotProps={{ textField: { sx: { minWidth: 140, maxWidth: 160 } } }}
              />
              <EasyCheckDatePicker
                label="To"
                value={filters.printDateTo}
                onChange={(date) => handleFilterChange('printDateTo', date)}
                format="dd-MM-yyyy"
                size="small"
                slotProps={{ textField: { sx: { minWidth: 140, maxWidth: 160 } } }}
              />
            </Stack>

            <Divider orientation="vertical" flexItem />

            {/* Cheque Date Range Group */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarMonth sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  Cheque Date
                </Typography>
              </Stack>
              <EasyCheckDatePicker
                label="From"
                value={filters.chqDateFrom}
                onChange={(date) => handleFilterChange('chqDateFrom', date)}
                format="dd-MM-yyyy"
                size="small"
                slotProps={{ textField: { sx: { minWidth: 140, maxWidth: 160 } } }}
              />
              <EasyCheckDatePicker
                label="To"
                value={filters.chqDateTo}
                onChange={(date) => handleFilterChange('chqDateTo', date)}
                format="dd-MM-yyyy"
                size="small"
                slotProps={{ textField: { sx: { minWidth: 140, maxWidth: 160 } } }}
              />
            </Stack>

            {/* Collected Date Range Group — only when the Cheque Collection module is enabled */}
            {collectionEnabled && (
              <>
                <Divider orientation="vertical" flexItem />
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <EventAvailable sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                      Collected Date
                    </Typography>
                  </Stack>
                  <EasyCheckDatePicker
                    label="From"
                    value={filters.collectedDateFrom}
                    onChange={(date) => handleFilterChange('collectedDateFrom', date)}
                    format="dd-MM-yyyy"
                    size="small"
                    slotProps={{ textField: { sx: { minWidth: 140, maxWidth: 160 } } }}
                  />
                  <EasyCheckDatePicker
                    label="To"
                    value={filters.collectedDateTo}
                    onChange={(date) => handleFilterChange('collectedDateTo', date)}
                    format="dd-MM-yyyy"
                    size="small"
                    slotProps={{ textField: { sx: { minWidth: 140, maxWidth: 160 } } }}
                  />
                </Stack>
              </>
            )}

            <Divider orientation="vertical" flexItem />

            {/* Bank Account Group */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AccountBalance sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  Bank
                </Typography>
              </Stack>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Bank</InputLabel>
                <Select
                  value={filters.bankName}
                  label="Bank"
                  onChange={(e) => handleFilterChange('bankName', e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxWidth: 500,
                        minWidth: 400,
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }
                      }
                    },
                  }}
                >
                  <MenuItem value="all">All Banks</MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.accountNumber}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}
                          title={`${bank.accountNumber} - ${bank.accountName}`}
                        >
                          {`${bank.accountNumber} - ${bank.accountName}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {bank.currency}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Divider orientation="vertical" flexItem />

            {/* User Filter */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  User
                </Typography>
              </Stack>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>User</InputLabel>
                <Select
                  value={filters.user}
                  label="User"
                  onChange={(e) => handleFilterChange('user', e.target.value)}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {distinctUsers.map((user) => (
                    <MenuItem key={user} value={user}>
                      {user}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Divider orientation="vertical" flexItem />

            {/* Status Filter */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <FilterList sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  Status
                </Typography>
              </Stack>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {['Pending', 'Printed', ...(signedStatusEnabled ? ['Signed'] : []), 'Cleared', 'Cancelled', 'Bounced', 'Collected'].map((status) => (
                    <MenuItem key={status} value={status}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLORS[status]?.color }} />
                        {status}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Action Buttons */}
            <Stack direction="row" spacing={1}>
              <EasyCheckButton
                buttonType="action"
                variant="outlined"
                onClick={resetFilters}
                startIcon={<ClearAll />}
                size="small"
              >
                Clear
              </EasyCheckButton>
              <EasyCheckButton
                buttonType="action"
                variant="contained"
                onClick={generateReport}
                startIcon={<Assessment />}
                size="small"
              >
                Apply Filters
              </EasyCheckButton>
            </Stack>
          </Stack>
        </Paper>

        {/* Status Count Summary — hidden for deleted cheques report */}
        {filters.reportType !== 'deleted' && <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {[
            { label: 'Cleared', count: statusCounts.cleared, bg: STATUS_COLORS.Cleared.bg, color: STATUS_COLORS.Cleared.color },
            { label: 'Uncleared', count: statusCounts.uncleared, bg: STATUS_COLORS.Pending.bg, color: STATUS_COLORS.Pending.color },
            { label: 'Cancelled', count: statusCounts.cancelled, bg: STATUS_COLORS.Cancelled.bg, color: STATUS_COLORS.Cancelled.color },
            { label: 'PDC', count: statusCounts.pdc, bg: '#e0f2fe', color: '#0284c7' },
            { label: 'Bounced', count: statusCounts.bounced, bg: STATUS_COLORS.Bounced.bg, color: STATUS_COLORS.Bounced.color },
          ].map((item) => (
            <Paper
              key={item.label}
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 1,
                backgroundColor: item.bg,
                border: `1px solid ${item.color}20`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: item.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {item.label}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: item.color, lineHeight: 1 }}>
                {item.count}
              </Typography>
            </Paper>
          ))}
        </Stack>}

        {/* Error Display */}
        {error && (
          <Card sx={{ mb: 2, bgcolor: 'error.lighter' }}>
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Data Grid */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: filters.reportType === 'crosstab' ? 'auto' : 'hidden',
        }}>
          <EasyCheckDataGridBasic
            rows={rows}
            columns={currentColumns}
            loading={loading}
            disableRowSelectionOnClick
            disableColumnFilter={false}
            rowHeight={52}
            paginationMode="client"
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            columnHeaderHeight={filters.reportType === 'crosstab' ? 72 : 56}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center" spacing={2}>
                  <Assessment sx={{ fontSize: 64, color: 'text.secondary' }} />
                  <Typography variant="h6" color="text.secondary">
                    No Records Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {allCheques.length === 0
                      ? 'No cheque data available'
                      : 'Try adjusting your filter criteria'}
                  </Typography>
                </Stack>
              ),
            }}
            sx={{
              height: '100%',
              width: filters.reportType === 'crosstab' ? `${Math.max(currentColumns.length * 140, 100)}px` : '100%',
              minWidth: filters.reportType === 'crosstab' ? `${Math.max(currentColumns.length * 140, 100)}px` : 'auto',
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${theme.palette.grey[100]}`,
                padding: '8px 12px',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                height: filters.reportType === 'crosstab' ? '72px' : '56px',
                minHeight: filters.reportType === 'crosstab' ? '72px !important' : '56px !important',
              },
              '& .MuiDataGrid-columnHeader': {
                color: theme.palette.text.primary,
                height: filters.reportType === 'crosstab' ? '72px !important' : '56px !important',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: theme.palette.grey[50],
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: `1px solid ${theme.palette.grey[200]}`,
                backgroundColor: theme.palette.grey[50],
                minHeight: '52px',
                maxHeight: '52px',
              },
            }}
          />
        </Box>
      </EasyCheckPageContent>
    </EasyCheckPageContainer>
  );
};

export default ReportsPage;
